import { Controller } from "@hotwired/stimulus"

/**
 * Email Pause Toggle Controller
 *
 * The Sending switch on a notification's card on the admin email pages. On means the
 * notification goes out; switching it off pauses its type for everyone, after a confirm.
 * Every card of the same type on the page (an email's variants) flips with it.
 *
 * Usage:
 *   <input type="checkbox"
 *          data-controller="email-pause-toggle"
 *          data-email-pause-toggle-url-value="/admin/emails/notifications_mailer/x/paused"
 *          data-email-pause-toggle-type-key-value="payments.completed"
 *          data-email-pause-toggle-label-value="Payment Receipt"
 *          data-action="change->email-pause-toggle#save">
 */
export default class extends Controller {
  static values = { url: String, typeKey: String, label: String }

  async save() {
    const input = this.element
    const paused = !input.checked

    if (paused && !window.confirm(`Pause ${this.labelValue}? Nobody will receive it, in-app or by email, until it's resumed.`)) {
      input.checked = true
      return
    }

    input.disabled = true
    const formData = new FormData()
    formData.append("_method", "PATCH")
    formData.append("paused", paused ? "1" : "0")

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        body: formData,
        headers: this.headers(),
        credentials: "same-origin"
      })

      // Only the JSON answer counts: a request turned away is redirected to an HTML page.
      const data = response.ok ? await response.json().catch(() => null) : null
      if (typeof data?.paused === "boolean") {
        this.apply(data.paused)
      } else if (response.ok) {
        input.checked = paused
        alert(`Couldn't update ${this.labelValue}. Only admins with full access can pause it.`)
      } else {
        input.checked = paused
        alert(`Couldn't update ${this.labelValue}: ${await this.errorMessage(response)}`)
      }
    } catch (error) {
      console.error("Error pausing email:", error)
      input.checked = paused
      alert("An error occurred. Please try again.")
    } finally {
      input.disabled = false
    }
  }

  // Sets every switch and card of this type on the page to the saved state.
  apply(paused) {
    document.querySelectorAll(`[data-email-pause-toggle-type-key-value="${CSS.escape(this.typeKeyValue)}"]`).forEach(toggle => {
      toggle.checked = !paused
      const card = toggle.closest(".admin-emails-item")
      if (!card) return

      card.classList.toggle("is-paused", paused)
      const state = card.querySelector(".admin-emails-item-state")
      if (state) state.textContent = paused ? "Paused" : "Sending"
    })
  }

  // The CSRF meta tag is left out where forgery protection is off (the test environment).
  headers() {
    const headers = { "Accept": "application/json" }
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    if (token) headers["X-CSRF-Token"] = token
    return headers
  }

  async errorMessage(response) {
    try {
      const data = await response.json()
      if (data?.errors?.length) return data.errors.join(", ")
    } catch (_) {
      // fall through
    }
    return `HTTP ${response.status}`
  }
}
